import { useEffect, useRef, type RefObject } from "react";
import { useLockStore } from "../../store/lockStore";
import { lockApi } from "../../api/lockApi";
import { asError, eventBus } from "@optimyl/events";

/**
 * Requester self-heal at countdown=0. Server-side auto-grant rides on the
 * holder's heartbeat; if the holder vanishes (logout-beacon-dropped, crash,
 * network drop) no heartbeat fires `AutoGrantIfExpired`, and the requester
 * would sit forever. When our countdown reaches 0 we drive the resolution
 * ourselves via POST /lock/claim — server attempts AutoGrant first, then
 * falls back to a plain acquire, and the response tells us what happened.
 *
 * On `granted` / `acquired`, drive `startGrantedTransition` directly from
 * the HTTP response — do NOT rely on the SignalR push to deliver. If SignalR
 * is mid-reconnect right now (plausible since this only fires when something
 * has already gone wrong), the push would be lost and the requester would
 * sit here forever. `startGrantedTransition` is idempotent, so the SignalR
 * push that *does* land later is harmless.
 *
 * The effect also resets `startGrantedTransition`'s idempotency guard
 * whenever lockStatus leaves "requesting" so a future re-request gets a
 * clean transition.
 */
export function useClaimStuckSelfHeal(
  enabled: boolean,
  lockStatus: string,
  countdownSeconds: number | null,
  formIdRef: RefObject<string | null>,
  sessionIdRef: RefObject<string>,
  startGrantedTransition: () => void,
  resetIdempotency: () => void,
): void {
  const setLockStatus = useLockStore((s) => s.setLockStatus);
  const setOutgoingStatus = useLockStore((s) => s.setOutgoingStatus);
  const setCountdown = useLockStore((s) => s.setCountdown);

  const claimFiredRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      claimFiredRef.current = false;
      resetIdempotency();
      return;
    }
    if (lockStatus !== "requesting") {
      claimFiredRef.current = false;
      // Once we leave "requesting" the transition is complete (or moot if we
      // bailed to locked-by-other). Reset so a future re-request gets a clean
      // grant transition.
      resetIdempotency();
      return;
    }
    if (countdownSeconds !== 0) return;
    if (claimFiredRef.current) return;
    claimFiredRef.current = true;

    const targetFormId = formIdRef.current;
    if (!targetFormId) return;

    (async () => {
      try {
        const result = await lockApi.claimStuckRequest(targetFormId, sessionIdRef.current);
        if (result.status === "granted" || result.status === "acquired") {
          setOutgoingStatus("Granted");
          startGrantedTransition();
        } else if (result.status === "still-held") {
          // Lock still held with no eligible grant — drop back to the lock screen.
          setLockStatus("locked-by-other", result.lockedBy ?? null);
          setOutgoingStatus(null);
          setCountdown(null);
        }
      } catch (err) {
        eventBus.publish("error", { error: asError(err), context: "useClaimStuckSelfHeal" });
      }
    })();
  }, [enabled, lockStatus, countdownSeconds, setLockStatus, setOutgoingStatus, setCountdown, startGrantedTransition, resetIdempotency, formIdRef, sessionIdRef]);
}
